import { PaginatedResult } from "@/Core/Client";
import instanceAxios from "@/Core/Client/AxiosConfig";
import {
    DragEndEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { arraySwap } from "@dnd-kit/sortable";
import { QueryKey, useQuery } from "@tanstack/react-query";
import {
    ColumnDef,
    ColumnFiltersState,
    ExpandedState,
    getCoreRowModel,
    getExpandedRowModel,
    getFacetedRowModel,
    getFacetedUniqueValues,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    RowPinningState,
    SortingState,
    TableOptions,
    useReactTable,
} from "@tanstack/react-table";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface ExtendTableOptions<T extends Record<string, unknown>>
    extends Omit<
        TableOptions<T>,
        "data" | "columns" | "getCoreRowModel" | "state"
    > {}

export function useTanStackTablePro<T extends Record<string, any>>({
    queryKey,
    endPoint,
    options,
    columnConfig,
}: {
    queryKey: QueryKey;
    endPoint: string;
    options?: ExtendTableOptions<T>;
    columnConfig: ColumnDef<T, any>[];
}) {
    const [queryStrings] = useSearchParams();

    const [columns] = React.useState(() => [...columnConfig]);

    const [globalFilter, setGlobalFilter] = React.useState("");
    const [sorting, setSorting] = React.useState<SortingState>([]);

    const [expanded, setExpanded] = React.useState<ExpandedState>({});
    const [columnOrder, setColumnOrder] = React.useState<string[]>(() =>
        columns.map((c) => c.id!),
    );

    const [columnFilters, setColumnFilters] =
        React.useState<ColumnFiltersState>([]);
    const [rowPinning, setRowPinning] = React.useState<RowPinningState>({
        top: [],
        bottom: [],
    });

    const { isPending, data, isFetched, isLoading, refetch } = useQuery<
        PaginatedResult<T>
    >({
        queryKey: [...queryKey, queryStrings.toString()],
        queryFn: () =>
            instanceAxios
                .get<PaginatedResult<T>>(endPoint, { params: queryStrings })
                .then((res) => res.data),
        // placeholderData: keepPreviousData,
    });

    // ===================================================================================================
    // these are custom functions dependent on dnd kit and react-table to handle Drag and Drop events
    const handleDragEndColumn = React.useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            setColumnOrder((columnOrder) => {
                const oldIndex = columnOrder.indexOf(active.id as string);
                const newIndex = columnOrder.indexOf(over.id as string);
                return arraySwap(columnOrder, oldIndex, newIndex);
            });
        }
    }, []);

    const handleDragEndRow = React.useCallback((event: DragEndEvent) => {
        const { active, over } = event;
        if (active && over && active.id !== over.id) {
            // setTableData((prevData) => {
            //     const oldIndex = prevData.findIndex(
            //         (item) => item.id === active.id,
            //     );
            //     const newIndex = prevData.findIndex(
            //         (item) => item.id === over.id,
            //     );
            //     return arraySwap(prevData, oldIndex, newIndex);
            // });
        }
    }, []);

    const sensors = useSensors(
        useSensor(MouseSensor, {}),
        useSensor(TouchSensor, {}),
        useSensor(KeyboardSensor, {}),
    );
    // these are custom functions dependent on dnd kit and react-table to handle Drag and Drop events
    // =================================================================================================

    const table = useReactTable({
        data: data?.items ?? [],
        columns,
        state: {
            sorting,
            expanded,
            rowPinning,
            columnOrder,
            globalFilter,
            columnFilters,
        },
        ...options,
        initialState: {
            pagination: {
                pageIndex: data?.page ?? 0,
                pageSize: data?.size ?? 0,
            },
        },
        pageCount: data?.totalPages ?? 0,
        manualPagination: true,
        rowCount: data?.totalItems ?? 0,
        paginateExpandedRows: true,
        getRowCanExpand: () => true,
        onSortingChange: setSorting,
        onExpandedChange: setExpanded,
        onRowPinningChange: setRowPinning,
        onColumnOrderChange: setColumnOrder,
        onGlobalFilterChange: setGlobalFilter,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFacetedRowModel: getFacetedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getFacetedUniqueValues: getFacetedUniqueValues(),
    });

    useEffect(() => {
        table.setPageSize(data?.size ?? 0);
        table.setPageIndex(data?.page ?? 0);

        table.options.pageCount = data?.totalPages;
        table.options.rowCount = data?.totalItems;
    }, [isFetched, data]);

    return {
        data,
        isPending,
        table,
        // setTableData,
        sensors,
        // tableData,
        isFetched,
        refetch,
        isLoading,
        rowPinning,
        columnOrder,
        globalFilter,
        setRowPinning,
        setColumnOrder,
        setGlobalFilter,
        handleDragEndRow,
        handleDragEndColumn,
    };
}
